import React, { useEffect, useState } from 'react';
import { Upload } from 'lucide-react';
import {
  EvvColumnMapping,
  EvvReconciliationService,
  EvvRunResponse,
  EvvUploadPreview
} from '../../../../services/evv/reconciliation.service';
import { COLUMN_FIELDS, errorCode, errorDetails, errorMessage } from './evvUi';

interface EvvUploadPanelProps {
  defaultTolerance: number;
  savedMapping: EvvColumnMapping;
  onCompleted: (run: EvvRunResponse) => void;
}

/**
 * Upload flow (EVV-05/06/07/16): check the file first (dry run, nothing stored), fix columns with
 * the mapping editor when HHAX renamed one, confirm when pending dates fall outside the CSV,
 * then run. Mapping and tolerance are saved by the backend only after a successful run.
 */
const EvvUploadPanel: React.FC<EvvUploadPanelProps> = ({ defaultTolerance, savedMapping, onCompleted }) => {
  const [file, setFile] = useState<File | null>(null);
  const [tolerance, setTolerance] = useState(String(defaultTolerance));
  const [mapping, setMapping] = useState<EvvColumnMapping>(savedMapping);
  const [mappingHeaders, setMappingHeaders] = useState<string[] | null>(null);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [preview, setPreview] = useState<EvvUploadPreview | null>(null);
  const [confirmRange, setConfirmRange] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setTolerance(String(defaultTolerance)), [defaultTolerance]);
  useEffect(() => setMapping(savedMapping), [savedMapping]);

  const toleranceValue = Number(tolerance);
  const toleranceInvalid = !Number.isInteger(toleranceValue) || toleranceValue < 0 || toleranceValue > 60;
  const mappingChanged = JSON.stringify(mapping) !== JSON.stringify(savedMapping);
  const options = () => ({
    toleranceMinutes: toleranceValue === defaultTolerance ? undefined : toleranceValue,
    columnMapping: mappingChanged ? mapping : undefined,
  });

  const reset = (next: File | null) => {
    setFile(next);
    setPreview(null);
    setError(null);
    setMissingColumns([]);
    setMappingHeaders(null);
  };

  const check = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const result = await EvvReconciliationService.previewCsv(file, options());
      setPreview(result);
      setMissingColumns([]);
      setMappingHeaders(null);
    } catch (err) {
      setError(errorMessage(err, 'The file could not be checked'));
      if (errorCode(err) === 'CSV_MISSING_COLUMNS') {
        const details = errorDetails(err) || {};
        setMappingHeaders(Array.isArray(details.headers) ? (details.headers as string[]) : []);
        setMissingColumns(Array.isArray(details.missing) ? (details.missing as string[]) : []);
      }
    } finally {
      setBusy(false);
    }
  };

  const run = async (acknowledgeOutOfRange: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setConfirmRange(false);
    try {
      const result = await EvvReconciliationService.uploadCsv(file, { ...options(), acknowledgeOutOfRange });
      reset(null);
      onCompleted(result);
    } catch (err) {
      if (errorCode(err) === 'OUT_OF_RANGE_UNCONFIRMED') setConfirmRange(true);
      setError(errorMessage(err, 'The reconciliation could not run'));
    } finally {
      setBusy(false);
    }
  };

  const warning = preview?.warnings?.[0];

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <p className="text-sm font-medium text-slate-700">Upload HHAX Billing Exceptions Export</p>
      <p className="mt-1 text-xs text-slate-500">CSV only, up to 5 MB. The file is checked before anything is saved.</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="inline-flex cursor-pointer items-center rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700">
          <Upload size={14} className="mr-2" />
          {file ? 'Change CSV' : 'Choose CSV'}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="evv-csv-input"
            onChange={(e) => reset(e.target.files?.[0] || null)}
          />
        </label>
        {file && <span className="max-w-[220px] truncate text-xs text-slate-600">{file.name}</span>}
        <label className="text-xs text-slate-600">
          Match tolerance (minutes)
          <input
            type="number"
            min={0}
            max={60}
            step={1}
            className="ml-2 w-16 rounded-md border border-slate-300 px-2 py-1 text-sm"
            value={tolerance}
            onChange={(e) => setTolerance(e.target.value)}
          />
        </label>
        <button
          type="button"
          onClick={check}
          disabled={!file || busy || toleranceInvalid}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {busy && !preview ? 'Checking…' : 'Check file'}
        </button>
      </div>
      {toleranceInvalid && <p className="mt-1 text-xs text-rose-700">Tolerance must be a whole number from 0 to 60.</p>}

      {error && <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700" role="alert">{error}</div>}

      {mappingHeaders && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="evv-column-mapping">
          <p className="text-sm font-medium text-amber-900">Map the HHAX columns</p>
          <p className="text-xs text-amber-800">
            Pick the column in your file for each field{missingColumns.length ? ` (missing: ${missingColumns.join(', ')})` : ''}.
            The mapping is saved for your agency after the next successful run.
          </p>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            {COLUMN_FIELDS.map(({ field, label, required }) => (
              <label key={field} className="text-xs text-slate-700">
                {label}{required ? ' *' : ''}
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                  value={mapping[field] || ''}
                  onChange={(e) => setMapping((prev) => ({ ...prev, [field]: e.target.value || undefined }))}
                >
                  <option value="">(use the standard name)</option>
                  {mappingHeaders.map((header) => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={check} disabled={busy} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Check again
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid="evv-preview">
          <p className="text-slate-700">
            {preview.rowCountParsed} of {preview.dataRowCount} rows read · HHAX dates {preview.csvActualDateRange.from} to {preview.csvActualDateRange.to}
            {' · '}tolerance ±{preview.toleranceMinutes} min
          </p>
          {preview.parseFailedCount > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              {preview.parseFailedCount} row(s) could not be read and will be skipped (rows {preview.parseFailed.map((f) => f.rowNumber).join(', ')}).
            </p>
          )}
          {warning && (
            <p className="mt-1 text-xs text-amber-700">
              {warning.count} pending visit(s) on {warning.dates.length} date(s) fall outside this file and will stay Pending: {warning.dates.join(', ')}.
            </p>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => (warning ? setConfirmRange(true) : run(false))}
              disabled={busy}
              className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy ? 'Reconciling…' : 'Run reconciliation'}
            </button>
          </div>
        </div>
      )}

      {confirmRange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h4 className="text-base font-semibold text-slate-800">Some pending dates are not in this file</h4>
            <p className="mt-2 text-sm text-slate-600">
              {warning ? `${warning.dates.join(', ')} will stay Pending.` : 'Pending visits fall outside the file and will stay Pending.'}
              {' '}Download a wider range from HHAX, or continue with this file.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmRange(false)} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">
                Cancel and re-download
              </button>
              <button type="button" onClick={() => run(true)} className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white">
                Continue with this file
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvvUploadPanel;
